// New Claim Flow - Constants & Configuration
// Animation specs, incident types, phase configs

import type {
  ClaimPhase,
  IncidentTypeConfig,
  PhaseConfig,
  ExpenseType,
  Currency,
} from './types';

// ============================================================================
// Animation Configuration
// ============================================================================

export const springConfig = {
  // Standard spring for most animations
  default: { type: 'spring' as const, stiffness: 300, damping: 30 },

  // Bouncy spring for celebratory/attention-grabbing animations
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 20 },

  // Gentle spring for subtle movements
  gentle: { type: 'spring' as const, stiffness: 200, damping: 25 },

  // Stiff spring for quick, snappy movements
  stiff: { type: 'spring' as const, stiffness: 500, damping: 35 },

  // Extra bouncy for celebrations
  celebration: { type: 'spring' as const, stiffness: 300, damping: 15 },
};

export const transitionConfig = {
  // Page/content transitions
  fade: { duration: 0.2, ease: 'easeInOut' },
  fadeIn: { duration: 0.3, ease: 'easeOut' },
  fadeOut: { duration: 0.2, ease: 'easeIn' },

  // Stagger delays for lists
  stagger: 0.1,
  staggerFast: 0.05,
  staggerSlow: 0.15,

  // Phase transition intro delays
  introDelay: 1500, // ms - for auto-transitioning intros
  shortIntroDelay: 1000,
};

export const animationVariants = {
  // Card enter animation
  cardEnter: {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: springConfig.default,
    },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.2 } },
  },

  // Floating action box animation
  floatingBox: {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { ...springConfig.default, delay: 0.2 },
    },
  },

  // Badge pop animation
  badgePop: {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: springConfig.bouncy,
    },
  },

  // Slide in from side
  slideInRight: {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
  },

  slideInLeft: {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
  },

  // Scale up from center
  scaleUp: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: springConfig.bouncy,
    },
  },

  // Subtle content swap
  contentSwap: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
  },
};

// ============================================================================
// Phase Configuration
// ============================================================================

export const phaseOrder: ClaimPhase[] = [
  'welcome',
  'extracting',
  'awaiting_email',
  'booking_review',
  'incident_intro',
  'incident_select',
  'incident_details',
  'evidence_intro',
  'documentation',
  'expenses',
  'narrative',
  'review',
  'submitting',
  'complete',
];

export const phaseConfig: Record<ClaimPhase, PhaseConfig> = {
  welcome: {
    id: 'welcome',
    canGoBack: false,
  },
  extracting: {
    id: 'extracting',
    canGoBack: false,
  },
  awaiting_email: {
    id: 'awaiting_email',
    canGoBack: true, // Can go back to welcome to upload instead
  },
  booking_review: {
    id: 'booking_review',
    canGoBack: false, // Can re-upload instead
    requiredFields: ['segments'],
  },
  incident_intro: {
    id: 'incident_intro',
    canGoBack: true,
    autoTransition: {
      to: 'incident_select',
      delay: transitionConfig.introDelay,
    },
  },
  incident_select: {
    id: 'incident_select',
    canGoBack: true,
  },
  incident_details: {
    id: 'incident_details',
    canGoBack: true,
    requiredFields: ['incidentType'],
  },
  evidence_intro: {
    id: 'evidence_intro',
    canGoBack: true,
    autoTransition: {
      to: 'documentation',
      delay: transitionConfig.shortIntroDelay,
    },
  },
  documentation: {
    id: 'documentation',
    canGoBack: true,
    canSkip: true,
  },
  expenses: {
    id: 'expenses',
    canGoBack: true,
    canSkip: true,
  },
  narrative: {
    id: 'narrative',
    canGoBack: true,
    canSkip: true,
  },
  review: {
    id: 'review',
    canGoBack: true,
  },
  submitting: {
    id: 'submitting',
    canGoBack: false,
  },
  complete: {
    id: 'complete',
    canGoBack: false,
  },
  error: {
    id: 'error',
    canGoBack: true,
  },
};

// Get previous phase for back navigation
export function getPreviousPhase(current: ClaimPhase): ClaimPhase | null {
  // Special cases
  if (current === 'incident_select') return 'booking_review';
  if (current === 'documentation') return 'incident_details';
  if (current === 'awaiting_email') return 'welcome'; // Go back to welcome to upload instead

  const currentIndex = phaseOrder.indexOf(current);
  if (currentIndex <= 0) return null;

  // Skip intro phases when going back
  let prevIndex = currentIndex - 1;
  while (prevIndex >= 0) {
    const prevPhase = phaseOrder[prevIndex];
    if (!prevPhase.includes('_intro')) {
      return prevPhase;
    }
    prevIndex--;
  }

  return null;
}

// Get next phase
export function getNextPhase(current: ClaimPhase): ClaimPhase | null {
  const currentIndex = phaseOrder.indexOf(current);
  if (currentIndex < 0 || currentIndex >= phaseOrder.length - 1) return null;
  return phaseOrder[currentIndex + 1];
}

// ============================================================================
// Incident Types Configuration
// ============================================================================

export const incidentTypes: IncidentTypeConfig[] = [
  {
    id: 'delay',
    label: 'Mein Flug hatte Verspätung',
    description: 'Der Flug ist deutlich später als geplant abgeflogen oder angekommen',
    icon: 'Clock',
    color: 'amber',
    followUpQuestion: 'Wie lange war die Verspätung? Wann sind Sie tatsächlich angekommen?',
    helpItems: [
      'Geplante vs. tatsächliche Abflugzeit',
      'Geplante vs. tatsächliche Ankunftszeit',
      'Erklärung der Fluggesellschaft (falls vorhanden)',
    ],
  },
  {
    id: 'cancel',
    label: 'Mein Flug wurde annulliert',
    description: 'Die Fluggesellschaft hat den Flug komplett gestrichen',
    icon: 'XCircle',
    color: 'red',
    followUpQuestion:
      'Wurden Sie auf einen anderen Flug umgebucht? Was geschah nach der Annullierung?',
    helpItems: [
      'Wann Sie von der Annullierung erfahren haben',
      'Welche Alternative angeboten wurde (falls vorhanden)',
      'Welche Flüge Sie tatsächlich genommen haben',
    ],
  },
  {
    id: 'denied_boarding',
    label: 'Mir wurde das Boarding verweigert',
    description: 'Ich durfte trotz gültigen Tickets nicht einsteigen',
    icon: 'Ban',
    color: 'orange',
    followUpQuestion: 'Welcher Grund wurde genannt? Wurde Ihnen eine Alternative angeboten?',
    helpItems: [
      'Der angegebene Grund für die Verweigerung',
      'Am Gate angebotene Entschädigung (falls vorhanden)',
      'Welcher Ersatztransport angeboten wurde',
    ],
  },
  {
    id: 'other',
    label: 'Etwas anderes ist passiert',
    description: 'Eine andere Art von Störung oder Problem',
    icon: 'HelpCircle',
    color: 'slate',
    followUpQuestion: 'Bitte beschreiben Sie, was passiert ist:',
    helpItems: [
      'Eine klare Beschreibung des Vorfalls',
      'Wie es Ihre Reisepläne beeinflusst hat',
      'Was die Fluggesellschaft zur Lösung unternommen hat',
    ],
  },
];

export function getIncidentConfig(type: string): IncidentTypeConfig | undefined {
  return incidentTypes.find((t) => t.id === type);
}

// ============================================================================
// Expense Configuration
// ============================================================================

export const expenseTypes: Array<{ value: ExpenseType; label: string; icon: string }> = [
  { value: 'hotel', label: 'Hotel / Unterkunft', icon: 'Building2' },
  { value: 'meal', label: 'Mahlzeit / Verpflegung', icon: 'UtensilsCrossed' },
  { value: 'transport', label: 'Transport', icon: 'Car' },
  { value: 'phone', label: 'Telefonkosten', icon: 'Phone' },
  { value: 'other', label: 'Sonstiges', icon: 'Receipt' },
];

export const currencies: Array<{ value: Currency; label: string; symbol: string }> = [
  { value: 'EUR', label: 'EUR', symbol: '\u20AC' },
  { value: 'USD', label: 'USD', symbol: '$' },
  { value: 'GBP', label: 'GBP', symbol: '\u00A3' },
  { value: 'CHF', label: 'CHF', symbol: 'CHF' },
];

// ============================================================================
// Booking Class Categories
// ============================================================================

export const bookingClassCategories = [
  'Economy',
  'Premium Economy',
  'Business',
  'First',
  'Suites',
] as const;

// ============================================================================
// Theme Types
// ============================================================================

export type ClaimTheme = 'invol' | 'drboese';

// ============================================================================
// Color Themes by Phase
// ============================================================================

export const phaseColors = {
  // Phase 1: Cool blue
  welcome: {
    gradient: 'from-slate-50 to-blue-50/30',
    accent: 'blue',
  },
  extracting: {
    gradient: 'from-slate-50 to-blue-50/30',
    accent: 'blue',
  },
  awaiting_email: {
    gradient: 'from-slate-50 to-blue-50/30',
    accent: 'blue',
  },
  booking_review: {
    gradient: 'from-slate-50 to-blue-50/30',
    accent: 'blue',
  },

  // Phase 2: Warm amber
  incident_intro: {
    gradient: 'from-slate-50 to-amber-50/20',
    accent: 'amber',
  },
  incident_select: {
    gradient: 'from-slate-50 to-amber-50/20',
    accent: 'amber',
  },
  incident_details: {
    gradient: 'from-slate-50 to-amber-50/20',
    accent: 'amber',
  },

  // Phase 3: Neutral
  evidence_intro: {
    gradient: 'from-slate-50 to-slate-100/50',
    accent: 'slate',
  },
  documentation: {
    gradient: 'from-slate-50 to-slate-100/50',
    accent: 'slate',
  },
  expenses: {
    gradient: 'from-slate-50 to-slate-100/50',
    accent: 'slate',
  },

  // Phase 4: Success green
  narrative: {
    gradient: 'from-slate-50 to-emerald-50/20',
    accent: 'emerald',
  },
  review: {
    gradient: 'from-slate-50 to-emerald-50/30',
    accent: 'emerald',
  },
  submitting: {
    gradient: 'from-slate-50 to-blue-50/30',
    accent: 'blue',
  },
  complete: {
    gradient: 'from-emerald-50/50 to-blue-50/30',
    accent: 'emerald',
  },

  // Error: Red
  error: {
    gradient: 'from-slate-50 to-red-50/20',
    accent: 'red',
  },
};

// ============================================================================
// Theme-Specific Phase Colors
// ============================================================================

export const themePhaseColors: Record<ClaimTheme, typeof phaseColors> = {
  invol: phaseColors, // Invol uses the default blue-based phase colors
  drboese: {
    // Dr. Böse uses yellow instead of blue for early phases
    welcome: {
      gradient: 'from-slate-50 to-yellow-50/20',
      accent: 'yellow',
    },
    extracting: {
      gradient: 'from-slate-50 to-yellow-50/20',
      accent: 'yellow',
    },
    awaiting_email: {
      gradient: 'from-slate-50 to-yellow-50/20',
      accent: 'yellow',
    },
    booking_review: {
      gradient: 'from-slate-50 to-yellow-50/20',
      accent: 'yellow',
    },
    // Keep amber for incident phases (both themes use similar warm tones here)
    incident_intro: {
      gradient: 'from-slate-50 to-amber-50/20',
      accent: 'amber',
    },
    incident_select: {
      gradient: 'from-slate-50 to-amber-50/20',
      accent: 'amber',
    },
    incident_details: {
      gradient: 'from-slate-50 to-amber-50/20',
      accent: 'amber',
    },
    // Neutral phases same for both
    evidence_intro: {
      gradient: 'from-slate-50 to-slate-100/50',
      accent: 'slate',
    },
    documentation: {
      gradient: 'from-slate-50 to-slate-100/50',
      accent: 'slate',
    },
    expenses: {
      gradient: 'from-slate-50 to-slate-100/50',
      accent: 'slate',
    },
    // Success phases - keep emerald for both
    narrative: {
      gradient: 'from-slate-50 to-emerald-50/20',
      accent: 'emerald',
    },
    review: {
      gradient: 'from-slate-50 to-emerald-50/30',
      accent: 'emerald',
    },
    submitting: {
      gradient: 'from-slate-50 to-yellow-50/20',
      accent: 'yellow',
    },
    complete: {
      gradient: 'from-emerald-50/50 to-yellow-50/30',
      accent: 'emerald',
    },
    error: {
      gradient: 'from-slate-50 to-red-50/20',
      accent: 'red',
    },
  },
};

// Helper to get phase colors for a theme
export function getPhaseColors(theme: ClaimTheme, phase: keyof typeof phaseColors) {
  return themePhaseColors[theme]?.[phase] || phaseColors[phase] || phaseColors.welcome;
}

// ============================================================================
// Theme-Specific Accent Colors for UI Elements
// ============================================================================

export interface ClaimThemeAccents {
  primary: string;
  primaryGradient: string;
  buttonPrimary: string;
  buttonHover: string;
  buttonShadow: string;
  buttonText: string;
  iconBg: string;
  iconColor: string;
  link: string;
  linkHover: string;
  border: string;
  bgLight: string;
  focusRing: string;
}

export const claimThemeAccents: Record<ClaimTheme, ClaimThemeAccents> = {
  invol: {
    primary: 'blue',
    primaryGradient: 'from-blue-500 to-purple-500',
    buttonPrimary: 'from-blue-500 to-blue-600',
    buttonHover: 'from-blue-600 to-blue-700',
    buttonShadow: 'shadow-blue-500/25',
    buttonText: 'text-white',
    iconBg: 'bg-gradient-to-br from-blue-500 to-purple-500',
    iconColor: 'text-white',
    link: 'text-blue-500',
    linkHover: 'hover:text-blue-600',
    border: 'border-blue-400',
    bgLight: 'bg-blue-50/50',
    focusRing: 'focus-visible:ring-blue-500',
  },
  drboese: {
    primary: 'yellow',
    primaryGradient: 'from-yellow-400 to-amber-500',
    buttonPrimary: 'from-[rgb(255,211,52)] to-amber-400',
    buttonHover: 'from-amber-400 to-amber-500',
    buttonShadow: 'shadow-yellow-500/25',
    buttonText: 'text-black',
    iconBg: 'bg-[rgb(255,211,52)]',
    iconColor: 'text-black',
    link: 'text-yellow-600',
    linkHover: 'hover:text-yellow-700',
    border: 'border-yellow-400',
    bgLight: 'bg-yellow-50/50',
    focusRing: 'focus-visible:ring-yellow-500',
  },
};

// Helper to get theme accents
export function getThemeAccents(theme: ClaimTheme): ClaimThemeAccents {
  return claimThemeAccents[theme] || claimThemeAccents.invol;
}

// ============================================================================
// Confetti Configuration
// ============================================================================

export const confettiConfig = {
  particleCount: 60,
  spread: 70,
  startVelocity: 30,
  decay: 0.95,
  gravity: 0.8,
  colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'],
  shapes: ['square', 'circle'] as const,
};

// ============================================================================
// Validation Messages
// ============================================================================

export const validationMessages = {
  pnr: {
    required: 'Buchungsreferenz (PNR) ist erforderlich',
    invalid: 'PNR sollte 5-7 Zeichen haben',
  },
  passenger: {
    firstNameRequired: 'Vorname ist erforderlich',
    lastNameRequired: 'Nachname ist erforderlich',
  },
  segment: {
    carrierRequired: 'Airline-Code ist erforderlich',
    carrierInvalid: 'Airline-Code sollte 2 Buchstaben haben',
    flightNoRequired: 'Flugnummer ist erforderlich',
    airportInvalid: 'Flughafen-Code sollte 3 Buchstaben haben',
    dateRequired: 'Datum ist erforderlich',
    timeRequired: 'Uhrzeit ist erforderlich',
  },
  incident: {
    typeRequired: 'Bitte wählen Sie aus, was passiert ist',
  },
  expense: {
    typeRequired: 'Bitte wählen Sie die Ausgabenart',
    amountRequired: 'Bitte geben Sie einen Betrag ein',
    amountInvalid: 'Betrag muss größer als 0 sein',
  },
};

// ============================================================================
// API Endpoints
// ============================================================================

export const apiEndpoints = {
  createCase: '/api/cases/',
  getCase: (id: string) => `/api/cases/${id}/`,
  applyExtraction: (id: string) => `/api/cases/${id}/apply-extraction/`,
  updateCase: (id: string) => `/api/cases/${id}/`,
  saveProgress: (id: string) => `/api/cases/${id}/intake/progress/`,
  expenses: (id: string) => `/api/cases/${id}/intake/expenses/`,
  narrative: (id: string) => `/api/cases/${id}/intake/narrative/`,
  complete: (id: string) => `/api/cases/${id}/intake/complete/`,
  uploadDocument: (id: string) => `/api/documents/cases/${id}/documents/`,
  categorizeDocument: (docId: string) => `/api/documents/${docId}/categorize/`,
};

// ============================================================================
// LocalStorage Keys
// ============================================================================

export const storageKeys = {
  draft: 'new-claim-draft',
  draftExpiry: 24 * 60 * 60 * 1000, // 24 hours in ms
};

// ============================================================================
// Incident-Specific Document Requirements
// ============================================================================

export type DocumentRequirementLevel = 'hard' | 'soft';

export interface DocumentRequirement {
  id: string;
  label: string;
  description: string;
  icon: string;
  level: DocumentRequirementLevel;
  satisfiedByKinds: string[]; // Document.kind values that satisfy this
  helpHint?: string; // Shown when requirement not met
}

export interface IncidentDocRequirements {
  incidentType: IncidentType;
  requirements: DocumentRequirement[];
  useFreeFormUpload?: boolean; // For "other" type
}

export const incidentDocumentRequirements: IncidentDocRequirements[] = [
  {
    incidentType: 'delay',
    requirements: [
      {
        id: 'proof_of_delay',
        label: 'Verspätungsnachweis',
        description: 'Verspätungsbescheinigung, Screenshot der Anzeigetafel, SMS/E-Mail',
        icon: 'Clock',
        level: 'hard',
        satisfiedByKinds: [
          'delay_certificate',
          'airline_response',
          'email_correspondence',
          'proof_of_delay',
        ],
        helpHint: 'Erforderlich um die Verspätung nachzuweisen',
      },
      {
        id: 'boarding_pass',
        label: 'Bordkarte',
        description: 'Original-Bordkarte für den verspäteten Flug',
        icon: 'Ticket',
        level: 'soft',
        satisfiedByKinds: ['boarding_pass_original', 'boarding_pass_actual', 'boarding'],
      },
      {
        id: 'airline_communication',
        label: 'Kommunikation mit Airline',
        description: 'E-Mails, Briefe oder Nachrichten von der Fluggesellschaft',
        icon: 'Mail',
        level: 'soft',
        satisfiedByKinds: ['airline_response', 'email_correspondence'],
      },
    ],
  },
  {
    incidentType: 'cancel',
    requirements: [
      {
        id: 'proof_of_cancellation',
        label: 'Stornierungsnachweis',
        description: 'Stornierungsmail, SMS-Screenshot, offizielle Mitteilung',
        icon: 'XCircle',
        level: 'hard',
        satisfiedByKinds: ['airline_response', 'email_correspondence', 'proof_of_cancellation'],
        helpHint: 'Erforderlich um die Annullierung nachzuweisen',
      },
      {
        id: 'new_itinerary',
        label: 'Neue Reiseroute',
        description: 'Wenn umgebucht: Bestätigung der neuen Flüge',
        icon: 'Route',
        level: 'soft',
        satisfiedByKinds: ['booking_confirmation', 'rerouting_itinerary'],
      },
      {
        id: 'boarding_pass',
        label: 'Bordkarte',
        description: 'Bordkarte(n) für annullierten oder Ersatzflug',
        icon: 'Ticket',
        level: 'soft',
        satisfiedByKinds: ['boarding_pass_original', 'boarding_pass_actual', 'boarding'],
      },
      {
        id: 'airline_communication',
        label: 'Kommunikation mit Airline',
        description: 'E-Mails, Briefe oder Nachrichten',
        icon: 'Mail',
        level: 'soft',
        satisfiedByKinds: ['airline_response', 'email_correspondence'],
      },
    ],
  },
  {
    incidentType: 'denied_boarding',
    requirements: [
      // No hard requirements - incident description captures key info
      {
        id: 'boarding_pass',
        label: 'Bordkarte',
        description: 'Bordkarte für den Flug bei dem Boarding verweigert wurde',
        icon: 'Ticket',
        level: 'soft',
        satisfiedByKinds: ['boarding_pass_original', 'boarding_pass_actual', 'boarding'],
      },
      {
        id: 'denial_notice',
        label: 'Ablehnungsbescheid',
        description: 'Schriftliche Dokumentation der Boardingverweigerung',
        icon: 'FileWarning',
        level: 'soft',
        satisfiedByKinds: ['airline_response', 'denial_notice', 'email_correspondence'],
      },
      {
        id: 'airline_communication',
        label: 'Kommunikation mit Airline',
        description: 'Schriftliche Mitteilung über die Verweigerung',
        icon: 'Mail',
        level: 'soft',
        satisfiedByKinds: ['airline_response', 'email_correspondence'],
      },
    ],
  },
  {
    incidentType: 'other',
    requirements: [],
    useFreeFormUpload: true,
  },
];

// Helper function to get document requirements for an incident type
export function getDocumentRequirements(
  incidentType: IncidentType | null
): IncidentDocRequirements {
  if (!incidentType)
    return { incidentType: 'other', requirements: [], useFreeFormUpload: true };
  return (
    incidentDocumentRequirements.find((r) => r.incidentType === incidentType) ?? {
      incidentType: 'other',
      requirements: [],
      useFreeFormUpload: true,
    }
  );
}

// Get only hard requirements for an incident type
export function getHardRequirements(incidentType: IncidentType | null): DocumentRequirement[] {
  return getDocumentRequirements(incidentType).requirements.filter((r) => r.level === 'hard');
}

// Get only soft requirements for an incident type
export function getSoftRequirements(incidentType: IncidentType | null): DocumentRequirement[] {
  return getDocumentRequirements(incidentType).requirements.filter((r) => r.level === 'soft');
}
